import { List, ListAlt } from '@mui/icons-material';
import { Checkbox, Fab, FormControlLabel, Grid, TextField, Tooltip } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { parseISO } from 'date-fns';
import { ChangeEvent, useEffect, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { If } from '../../common/IfComponent';
import { RootState } from '../../store';
import { toDateString } from '../../utils';
import { ExternalPricelistDialog } from '../externalprice/ExternalPricelistDialog';
import PricesMenuButton from '../price/PricesMenuButton';
import { PricelistDialog } from '../pricelist/PricelistDialog';
import WarehouseSelector from '../stock/WarehouseSelector';
import {
    getAgreementPriceTable,
    getAgreementShowAllArticles,
    getSelectedAgreement,
    getSelectedAgreementPricelistDate,
    getSelectedAgreementWarehouse,
    getSelectedAgreementWarehouseDate,
    getSelectedRefExtPricelistId,
    getSelectedRefPricelistId
} from './redux/agreementSelectors';
import {
    agreementPriceTableActions,
    agreementStockDateSelected,
    agreementWarehouseSelected,
    pricelistDateSelected,
    refExtPricelistIdSelected,
    refPricelistIdSelected,
    setIsExporting,
    showAllArticlesChanged
} from './redux/agreementSlices';

const mapStateToProps = (state: RootState) => ({
    selectedRefDate: getSelectedAgreementPricelistDate(state),
    selectedRefPricelistId: getSelectedRefPricelistId(state),
    selectedRefExtPricelistId: getSelectedRefExtPricelistId(state),
    selectedAgreement: getSelectedAgreement(state),
    selectedAgreementWarehouse: getSelectedAgreementWarehouse(state),
    selectedAgreementStockDate: getSelectedAgreementWarehouseDate(state),
    showAllArticles: getAgreementShowAllArticles(state),
    agreementPriceTable: getAgreementPriceTable(state)
});
type Props = ReturnType<typeof mapStateToProps>;

const AgreementButtons = ({
    selectedRefDate,
    selectedRefPricelistId,
    selectedRefExtPricelistId,
    selectedAgreement,
    selectedAgreementWarehouse,
    selectedAgreementStockDate,
    showAllArticles,
    agreementPriceTable
}: Props) => {
    const dispatch = useDispatch();

    const handleSetRefDate = (date: Date | null) => {
        if (date) {
            dispatch(pricelistDateSelected(toDateString(date)));
        }
    };
    const [showPricelistDialog, setShowPricelistDialog] = useState(false);
    const [showExternalPricelistDialog, setShowExternalPricelistDialog] = useState(false);
    const [showDateButtons, setShowDateButtons] = useState(false);
    const handleSetSelectedStockDate = (stockDate: Date) => {
        dispatch(agreementStockDateSelected(toDateString(stockDate)));
    };
    const handleSelectedRefPricelist = (pricelistId: number) => {
        dispatch(refPricelistIdSelected(pricelistId));
    };
    const handleSelectedExtRefPricelist = (externalPricelistId: number[]) => {
        dispatch(refExtPricelistIdSelected(externalPricelistId[0]));
    };

    const handleSelectWarehouse = (value: unknown) => {
        dispatch(agreementWarehouseSelected(value as number));
    };

    const handleShowAllArticles = (event: ChangeEvent<HTMLInputElement>) => {
        dispatch(showAllArticlesChanged(event.target.checked));
    };
    useEffect(() => {
        if (selectedRefPricelistId != null || selectedRefExtPricelistId != null) {
            setShowDateButtons(true);
        }
    }, [selectedRefExtPricelistId, selectedRefPricelistId]);

    return (
        <>
            <If
                condition={showPricelistDialog}
                then={
                    <PricelistDialog
                        multiSelect={false}
                        pricelistsSelected={ref => handleSelectedRefPricelist(ref as number)}
                        selectedPricelistIds={selectedRefPricelistId ?? undefined}
                        handleClose={() => setShowPricelistDialog(false)}
                    />
                }
                else={null}
            />
            <If
                condition={showExternalPricelistDialog}
                then={
                    <ExternalPricelistDialog
                        handleExternalPricelistSelected={ref => handleSelectedExtRefPricelist(ref)}
                        multiSelect={false}
                        selectedExtPricelistIds={[selectedRefExtPricelistId ?? 0]}
                        handleClose={() => setShowExternalPricelistDialog(false)}
                    />
                }
                else={null}
            />

            <Grid container alignItems="flex-start" direction="row" wrap="nowrap" spacing={2}>
                <Grid item sx={theme => ({ marginRight: theme.spacing(4) })}>
                    <Grid container spacing={2}>
                        <Grid item>
                            <Tooltip title="Choose ref. pricelist">
                                <span>
                                    <Fab
                                        size="small"
                                        color="primary"
                                        sx={theme => ({ marginTop: theme.spacing(1) })}
                                        onClick={() => setShowPricelistDialog(true)}
                                    >
                                        <List />
                                    </Fab>
                                </span>
                            </Tooltip>
                        </Grid>
                        <If
                            condition={selectedAgreement?.handleExternalPrices ?? false}
                            then={
                                <Grid item>
                                    <Tooltip title="Choose ref. external pricelist">
                                        <span>
                                            <Fab
                                                size="small"
                                                color="primary"
                                                sx={theme => ({ marginTop: theme.spacing(1) })}
                                                onClick={() => setShowExternalPricelistDialog(true)}
                                            >
                                                <ListAlt />
                                            </Fab>
                                        </span>
                                    </Tooltip>
                                </Grid>
                            }
                            else={null}
                        />
                    </Grid>
                </Grid>

                <Grid item>
                    <If
                        condition={showDateButtons}
                        then={
                            <>
                                <DatePicker<Date>
                                    showToolbar={false}
                                    inputFormat="yyyy-MM-dd"
                                    value={parseISO(selectedRefDate)}
                                    onChange={val => handleSetRefDate(val)}
                                    label="Pricelist date"
                                    renderInput={params => (
                                        <TextField
                                            sx={theme => ({ marginTop: theme.spacing(1), width: 150 })}
                                            size="small"
                                            label="Pricelist date"
                                            variant="outlined"
                                            {...params}
                                        />
                                    )}
                                />
                            </>
                        }
                        else={null}
                    />
                </Grid>
                <Grid item>
                    <WarehouseSelector
                        selectedWarehouse={selectedAgreementWarehouse}
                        onSetWarehouse={handleSelectWarehouse}
                    />
                </Grid>
                <Grid item>
                    <DatePicker
                        showToolbar={false}
                        inputFormat="yyyy-MM-dd"
                        label="Stock date"
                        value={selectedAgreementStockDate}
                        onChange={date => handleSetSelectedStockDate(parseISO(date ?? '') as Date)}
                        renderInput={params => (
                            <TextField
                                sx={theme => ({ marginTop: theme.spacing(1), width: 150 })}
                                size="small"
                                label="Stock date"
                                variant="outlined"
                                {...params}
                            />
                        )}
                    />
                </Grid>
                <Grid item sx={{ justifyItems: 'center' }}>
                    <FormControlLabel
                        control={<Checkbox checked={showAllArticles} onChange={handleShowAllArticles} />}
                        label="Show all articles"
                    />
                </Grid>
                <Grid item>
                    <PricesMenuButton
                        onDownloadClicked={() => dispatch(setIsExporting(true))}
                        showTooltipsChangedAction={agreementPriceTableActions.showTooltipsChanged}
                        tooltipsVisible={agreementPriceTable.showTooltips}
                    />
                </Grid>
            </Grid>
        </>
    );
};

export default connect(mapStateToProps)(AgreementButtons);
